import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        setPost(res.data);
      } catch {
        setError(true);
      }
    })();
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <SEO title={t("blog.notFound")} path={`/insights/${slug}`} />
        <div className="text-center">
          <div className="font-heading text-3xl text-[#0A192F]">{t("blog.notFound")}</div>
          <Link to="/" className="mt-4 inline-block text-[#1E3A8A] underline">{t("legal.back")}</Link>
        </div>
      </div>
    );
  }

  if (!post) return <div className="min-h-screen flex items-center justify-center text-slate-500">{t("blog.loading")}</div>;

  return (
    <div className="font-body bg-white text-slate-900" data-testid="blog-detail-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/insights/${post.slug}`}
        image={post.image_url}
      />
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-lg sm:text-xl text-[#0A192F]">X10 Management GmbH</Link>
          <Link to="/#insights" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A192F] hover:text-[#1E3A8A]" data-testid="blog-back-link">
            <ArrowLeft size={16} /> {t("blog.back")}
          </Link>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1E3A8A]">{post.category}</div>
        <h1 className="mt-6 font-heading font-bold text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight">{post.title}</h1>
        <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
          <span>{post.author}</span>
          <span className="w-1 h-1 rounded-full bg-slate-300" />
          <span>{post.read_time}</span>
        </div>

        <div className="mt-10 aspect-[16/9] rounded-2xl overflow-hidden bg-slate-100">
          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="mt-12 max-w-none text-lg leading-relaxed text-slate-700">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] mt-12 mb-4" {...props} />,
              h2: ({ node, ...props }) => <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] mt-10 mb-4" {...props} />,
              h3: ({ node, ...props }) => <h3 className="font-heading text-xl sm:text-2xl font-semibold text-[#0A192F] mt-8 mb-3" {...props} />,
              p: ({ node, ...props }) => <p className="my-5 leading-relaxed" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc pl-6 my-5 space-y-2" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal pl-6 my-5 space-y-2" {...props} />,
              li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
              a: ({ node, ...props }) => <a className="text-[#1E3A8A] underline hover:text-[#3B82F6]" target="_blank" rel="noopener noreferrer" {...props} />,
              strong: ({ node, ...props }) => <strong className="font-semibold text-[#0A192F]" {...props} />,
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-200 flex flex-wrap items-center justify-between gap-4">
          <Link to="/#insights" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A192F]">
            <ArrowLeft size={16} /> {t("blog.anchor")}
          </Link>
          <Link to="/#contact" className="px-6 py-3 bg-[#0A192F] text-white rounded-full font-semibold hover:bg-[#1E3A8A] transition-colors">
            {t("blog.cta")}
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
}
